import { create } from 'zustand';
import { applicationService } from 'src/services/application';
import { Application, SubscriptionPlan } from 'src/services/interfaces/application';
import { useInsuranceStore } from './insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { DEFAULT_TIER } from '@features/onboarding/constants';

interface ApplicationState {
  applications: Application[];
  isLoading: boolean;
  isCreated: boolean;
  error: string | null;
  status?: 'created' | 'pending' | 'approved' | 'rejected';
  currentApplicationId?: number | string;
  isMainPlanRejected?: boolean;
  activeSubscriptionPlans: SubscriptionPlan[];
  activeCategories: string[];
  isAllRejected: boolean;
  currentApplicationStatus: 0 | 1;
  signedAt: string | null;
  createApplication: (data: Application) => Promise<void>;
  updateApplication: (data: Application) => Promise<void>;
  submitApplication: (
    id: number,
    data: { paymentMethodId?: string }
  ) => Promise<void>;
  acceptApplication: () => Promise<void>;
  rejectApplication: () => Promise<void>;
  getCurrentApplication: () => Promise<void>;
  updateApplicationById: (id: number | string, data: Application) => Promise<void>;
  setIsMainPlanRejected: (isMainPlanRejected: boolean) => void;
  setIsAllRejected: (isAllRejected: boolean) => void;
  fetchActiveSubscriptionPlans: () => Promise<void>;
  updateTierFromApplicationState: () => Promise<void>;
  tier: string;
}

export const useApplicationStore = create<ApplicationState>((set) => ({
  applications: [],
  isLoading: false,
  isCreated: false,
  tier: DEFAULT_TIER,

  isMainPlanRejected: false,
  activeSubscriptionPlans: [],
  error: null,
  activeCategories: [],
  isAllRejected: false,
  currentApplicationStatus: 0,
  signedAt: null,

  // Устанавливаем флаг, опираясь на уже загруженные activeSubscriptionPlans.
  // Никаких сетевых запросов здесь не выполняем, чтобы избежать лишних вызовов API.
  setIsMainPlanRejected: (rejected: boolean) => {
    const { activeSubscriptionPlans } = useApplicationStore.getState();
    const fullPlans = useInsuranceStore.getState().plans;

    const hasMajorMedical = (() => {
      if (activeSubscriptionPlans.length === 0) return false;
      const activeIds = activeSubscriptionPlans.map((p) => p.planId);
      return fullPlans.some(
        (plan) => activeIds.includes(plan.id) && plan.category === 'Major Medical'
      );
    })();

    set({ isMainPlanRejected: hasMajorMedical || rejected });
  },
  setIsAllRejected: (isAllRejected: boolean) => {
    set({ isAllRejected });
  },

  fetchActiveSubscriptionPlans: async () => {
    try {
      const activeSubscriptionPlans = await applicationService.getActiveSubscriptionPlans();
      const fullPlans = useInsuranceStore.getState().plans;
      const activeCategories = fullPlans.filter(plan => activeSubscriptionPlans.map(item => item.planId).includes(plan?.id || 0)).map(item => item.category);
      const allCategories = fullPlans.map(item => item.category);
      // isAllRejected = true только если пользователь купил планы из ВСЕХ доступных категорий
      const isAllRejected = allCategories.length > 0 && allCategories.every(category => activeCategories.includes(category));
      set({ activeSubscriptionPlans, activeCategories, isAllRejected });
    } catch (error) {
      console.error('Failed to fetch active subscription plans:', error);
    }
  },

  getCurrentApplication: async () => {
    set({ isLoading: true, error: null });
    try {
      const applicationData = await applicationService.getApplications();
      const { payload, id, status, signature } = applicationData;
      
      if (id) {
        set({ currentApplicationId: id });
      }
      
      // Set signedAt from signature data
      const signedAt = signature?.signedAt || null;
      set({ signedAt });
      
      if (!payload) {
        set({ isLoading: false, status: 'created' });
      } else {
        // payload
        let activeSubscriptionPlans: SubscriptionPlan[] = [];
        if (payload.plans && payload.plans.length > 0) {
          activeSubscriptionPlans = await applicationService.getActiveSubscriptionPlans();
          payload.plans = payload.plans.filter(plan => !(activeSubscriptionPlans.map(item => item.planId).includes(plan.id)));
        }
        console.log('payload', payload);
        set({ applications: [payload], isLoading: false, status: 'created' })
      }
    } catch (error) {
      set({ error: 'Failed to fetch current application.', isLoading: false });
    }
  },

  createApplication: async (data: Application) => {
    set({ isLoading: true, error: null });
    try {
      const application = await applicationService.createApplication(data);
      set({
        currentApplicationId: application.id,
        status: 'created',
        applications: [
          ...useApplicationStore.getState().applications,
          application,
        ],
        isLoading: false,
        isCreated: true,
      });
      // const { activeSubscriptionPlans } = useApplicationStore.getState();
      // if(useApplicationStore.getState().isMainPlanRejected || activeSubscriptionPlans.length > 0){
      //   window.location.reload();
      // }
    } catch (error) {
      set({ error: 'Failed to create application.', isLoading: false });
    }
  },

  updateApplication: async (data: Application) => {
    set({ isLoading: true, error: null });
    try {
      const application = await applicationService.updateApplication(data);
      set({
        applications: useApplicationStore
          .getState()
          .applications.map((item) => (item === data ? application : item)),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to update application.', isLoading: false });
    }
  },

  updateApplicationById: async (id: number | string, data: Application) => {
    set({ isLoading: true, error: null });
    try {
      const isMainPlanRejected = useApplicationStore.getState().isMainPlanRejected;
      console.log('isMainPlanRejected', isMainPlanRejected);
      if (isMainPlanRejected) {
        const fullPlans = useInsuranceStore.getState().plans;
        const newPlans = []
        if (fullPlans.length > 0) {
          const dataPlans = [...data.plans];
          for (const plan of dataPlans) {
            const myMajorPlan = fullPlans.find(p => p.category === 'Major Medical' && p.id === plan.id)?.prices.find(item => item.id === plan.priceId)
            if (!myMajorPlan) {
              newPlans.push(plan);
            }
          }
          data.plans = dataPlans;
        }
        data.plans = newPlans;
      }
      await applicationService.updateApplicationById(id, data);
      const activeSubscriptionPlans = await applicationService.getActiveSubscriptionPlans();
      set({ activeSubscriptionPlans });
    } catch (error) {
      set({ error: 'Failed to update application by id.', isLoading: false });
    } finally {
      await useApplicationStore.getState().getCurrentApplication();
    }
  },

  submitApplication: async (
    id: number,
    data: { paymentMethodId?: string }
  ) => {
    set({ isLoading: true, error: null });
    try {
      await applicationService.submitApplication(id, data);
      await useApplicationStore.getState().fetchActiveSubscriptionPlans();
      set({ isLoading: false });
    } catch (error) {
      set({ error: 'Failed to submit application.', isLoading: false });
    }
  },

  acceptApplication: async () => {
    set({ isLoading: true, error: null });
    try {
      await applicationService.acceptApplication();
      set({ isLoading: false });
    } catch (error) {
      set({ error: 'Failed to accept application.', isLoading: false });
    }
  },

  rejectApplication: async () => {
    set({ isLoading: true, error: null });
    try {
      await applicationService.rejectApplication();
      set({ isLoading: false });
    } catch (error) {
      set({ error: 'Failed to reject application.', isLoading: false });
    }
  },

  updateTierFromApplicationState: async () => {
    try {
      const applicationState = await applicationService.applicationState();
      if (applicationState.acceptedPriceId) {
        // Find tier from acceptedPriceId in insurance plans
        const allPlans = useInsuranceStore.getState().plans;
        let foundTier = DEFAULT_TIER;
        
        for (const plan of allPlans) {
          const price = plan.prices.find(p => p.id === applicationState.acceptedPriceId);
          if (price && price.tier) {
            foundTier = price.tier;
            break;
          }
        }

        set({ tier: foundTier });
      } else {
        set({ tier: DEFAULT_TIER });
      }
    } catch (error) {
      console.error('Failed to update tier from application state:', error);
      set({ tier: DEFAULT_TIER });
    }
  },
}));
